import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
	LocalizedTextIds,
	Mode,
	SectorWithChildOptions,
} from 'company-finder-common';
import {
	ComponentBase,
	DeploymentContext,
	SearchService,
} from '../../../../../_common';

@Component({
	selector: 'sector-selector',
	templateUrl: './sector-selector.component.html',
	styleUrls: ['./sector-selector.component.scss'],
	standalone: false,
})
export class SectorSelectorComponent extends ComponentBase implements OnInit {
	@Input()
	public includeCounts: boolean = true;

	@Input()
	public showFilter: boolean = false;

	@Input()
	public isPrimary: boolean = true;

	@Input()
	public otherField: 'subsector' | 'developmentStage' | 'focusArea' =
		'subsector';

	@Input()
	public selectableSectors: boolean = true;

	@Input()
	public optionData: SectorWithChildOptions[];
	@Output() selectionChange = new EventEmitter<SectorWithChildOptions[]>();

	constructor(dc: DeploymentContext, private searchService: SearchService) {
		super(dc);
	}

	public searchText: string = '';
	protected _selectedItems: SectorWithChildOptions[] = [];
	public ngOnInit(): void {
		this._selectedItems = this.getSelections();

		// If sectors cannot be selected, start with them open
		// to avoid a menu that has no meaningful options visible
		if (!this.selectableSectors) {
			for (const option of this.optionData.filter(
				(option) => !option.parentSector
			)) {
				this.toggleOpenChildOptions(option.sectorOrOption);
			}
		}
	}
	onSearchChange(event) {
		this.searchText = event.target.value?.toLowerCase();
	}

	// Keep track of collapsed state of children by the parent
	// The default state is collapsed (not open)
	public openChildOptions: { [id: string]: boolean } = {};
	public compareProperty = 'sectorId';

	public isVisible(item: SectorWithChildOptions): boolean {
		return !item.parentSector || this.openChildOptions[item.parentSector];
	}

	public getSelections(): SectorWithChildOptions[] {
		switch (this.otherField) {
			case 'subsector': {
				return this.isPrimary
					? [
							...this.optionData.filter((s1) =>
								this.searchService.filter.primarySectors.some(
									(s2) => s1.sectorOrOption === s2
								)
							),
							...this.optionData.filter((s1) =>
								this.searchService.filter.primarySubSectors.some(
									(s2) => s1.sectorOrOption === s2
								)
							),
					  ]
					: [
							...this.optionData.filter((s1) =>
								this.searchService.filter.secondarySectors.some(
									(s2) => s1.sectorOrOption === s2
								)
							),
							...this.optionData.filter((s1) =>
								this.searchService.filter.secondarySubSectors.some(
									(s2) => s1.sectorOrOption === s2
								)
							),
					  ];
			}
			case 'developmentStage':
				return this.optionData.filter((opt) =>
					this.searchService.filter.developmentStages.includes(
						opt.sectorOrOption
					)
				);
			case 'focusArea':
				return this.optionData.filter((opt) =>
					this.searchService.filter.focusAreas.includes(
						opt.sectorOrOption
					)
				);
			default: {
				return [];
			}
		}
	}

	public get filteredData(): SectorWithChildOptions[] {
		return this.optionData.filter(
			(sector) =>
				(sector.children?.some((sub) =>
					sub.toLowerCase().includes(this.searchText.toLowerCase())
				) ||
					sector.sectorOrOption
						.toLowerCase()
						.includes(this.searchText.toLowerCase())) &&
				this.isVisible(sector)
		);
	}

	public get placeholder(): string {
		return this.Loc(
			LocalizedTextIds.FilterListBySector,
			this.isPrimary ? '' : 'secondary '
		).replace('sub-sector', this.otherField);
	}

	public isSelected(item: SectorWithChildOptions) {
		return this._selectedItems.some(
			(selectedItem) =>
				selectedItem.sectorOrOption === item.sectorOrOption
		);
	}

	public isExpandable(sectorAndCount: SectorWithChildOptions): boolean {
		return (
			!sectorAndCount.parentSector &&
			(this.mode === Mode.Company || this.otherField !== 'subsector')
		);
	}

	public handleLabelClick(
		sectorAndCount: SectorWithChildOptions,
		ev?: Event
	) {
		ev?.preventDefault();
		if (this.isExpandable(sectorAndCount)) {
			this.toggleOpenChildOptions(sectorAndCount.sectorOrOption);
		} else {
			this.updateSelection(sectorAndCount, ev);
		}
	}

	public toggleOpenChildOptions(sectorName: string, _ev?: Event): void {
		this.openChildOptions[sectorName] = !this.openChildOptions[sectorName];
	}

	public isInderminate(item: SectorWithChildOptions): boolean {
		return (
			!this._selectedItems.some(
				(sector) => sector.sectorOrOption === item.sectorOrOption
			) &&
			this._selectedItems.some(
				(sector) => sector.parentSector === item.sectorOrOption
			)
		);
	}

	public updateSelection(item: SectorWithChildOptions, ev?: Event): void {
		ev?.stopPropagation();

		if (!item.parentSector && !this.selectableSectors) {
			return;
		}

		if (
			!this._selectedItems.some(
				(sector) => sector.sectorOrOption === item.sectorOrOption
			)
		) {
			this._selectedItems.push(item);
		} else {
			this._selectedItems = this._selectedItems.filter(
				(sector) => sector.sectorOrOption !== item.sectorOrOption
			);
		}

		this.selectionChange.emit(this._selectedItems);
	}

	public trackBySector(_index: number, item: SectorWithChildOptions): string {
		return `${item.parentSector}${item.sectorOrOption}`;
	}
}
